// React Query hooks — desacoplam components de fetch logic.
// Auth: Clerk session cookie é enviado automaticamente em fetch /api/* same-origin.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (r.status === 401) {
    window.location.href = '/sign-in';
    throw new Error('UNAUTHORIZED');
  }
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${r.status}`);
  }
  return r.json();
}

// ─── ME ──────────────────────────────
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<{ user: { id: string; email: string; nome: string | null } }>('/api/me'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// ─── SOCIOS ──────────────────────────
type SocioSummary = { slug: string; nome: string; papel: string; classe: string; foto: string; ordem: number; level: number };

export function useSocios() {
  return useQuery({
    queryKey: ['socios'],
    queryFn: async () => (await apiFetch<{ data: SocioSummary[] }>('/api/socios')).data,
  });
}

export function useSocio(slug: string) {
  return useQuery({
    queryKey: ['socio', slug],
    queryFn: async () => (await apiFetch<{ data: any }>(`/api/socios/${slug}`)).data,
    enabled: !!slug && slug !== 'new',
  });
}

export function useUpsertSocio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: any) =>
      apiFetch<{ ok: boolean }>('/api/socios', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['socios'] });
      if (vars?.slug) qc.invalidateQueries({ queryKey: ['socio', vars.slug] });
    },
  });
}

export function useDeleteSocio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<{ ok: boolean }>(`/api/socios/${slug}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['socios'] }),
  });
}
